import { mapToMittVerktoyItem } from '@components/verktoy-details/utils/verktoyMapper';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { LaeringsaktivitetStatusType, type MittVerktoyItem, type Verktoy } from '@src/types/minhelseentities';

import type { VerktoyFilterState, VerktoyState } from '@src/types/stateTypes';

import {
  addLaeringsaktivitetThunk,
  avvisOppgaveLaeringsaktivitetThunk,
  fetchMineVerktoyV2Thunk,
  fetchMittVerktoyByGuidThunk,
  fetchVerktoyListeThunk,
  fetchVerktoyThunk,
  removeLaeringsaktivitetThunk,
} from '../thunks';

export type VerktoyStatePayload = {
  verktoyFilters?: VerktoyFilterState;
  isFilterOpen?: boolean;
};
export const getDefaultVerktoyFilters = (): VerktoyFilterState => {
  return {
    SokeString: '',
    IsAnonymCheckboxChecked: false,
    Fagomrader: [],
    VerktoyTyper: [],
    Malgrupper: [],
  };
};
export const initialState: VerktoyState = {
  error: undefined,
  verktoyListe: [],
  mineVerktoyListe: [],
  verktoy: undefined,
  mittVerktoyByGuid: undefined,
  mittVerktoyByGuidLoading: false,
  verktoyListeLoading: false,
  laeringsaktiviteterLoading: false,
  verktoyLoading: false,
  isWaitingForAktivitetResponse: false,
  isOppgaveAvvisSuccess: false,
  isOppgaveEnrollmentSuccess: false,
  addedAktivitetName: '',
  aktivitetToRemove: undefined,
  aktivitetToRemoveMineVerktoy: undefined,
  isFilterOpen: false,
  verktoyFilters: {
    SokeString: '',
    IsAnonymCheckboxChecked: false,
    Fagomrader: [],
    VerktoyTyper: [],
    Malgrupper: [],
  },
};
const reducer = createSlice({
  name: 'Verktoy',
  initialState,
  reducers: {
    setVerktoyFilters: (state, action: PayloadAction<{ verktoyFilters?: VerktoyFilterState }>) => {
      state.verktoyFilters = action.payload.verktoyFilters || initialState.verktoyFilters;
    },
    setIsFilterOpen: (state, action: PayloadAction<{ isFilterOpen: boolean }>) => {
      state.isFilterOpen = !!action.payload.isFilterOpen;
    },
    clearError: state => {
      state.error = undefined;
    },
    closeLightboxes: state => {
      state.addedAktivitetName = '';
      state.aktivitetToRemove = undefined;
      state.aktivitetToRemoveMineVerktoy = undefined;
    },
    showRemoveAktivitetConfirmation: (state, action: PayloadAction<{ verktoy: Verktoy }>) => {
      state.aktivitetToRemove = action.payload.verktoy;
    },
    showRemoveAktivitetConfirmationMineVerktoy: (state, action: PayloadAction<{ MittVerktoyItem: MittVerktoyItem }>) => {
      state.aktivitetToRemoveMineVerktoy = action.payload.MittVerktoyItem;
    },
    setStartAktivitet: (state, action: PayloadAction<{ aktivitetId: number }>) => {
      const nyLaeringsaktivitetListe = state.mineVerktoyListe.map(item => {
        return item.LaeringsaktivitetId && item.LaeringsaktivitetId === action.payload.aktivitetId
          ? { ...item, Laeringsaktivitet: { ...item, status: LaeringsaktivitetStatusType.Started } }
          : item;
      });
      return {
        ...state,
        mineVerktoyListe: nyLaeringsaktivitetListe,
      };
    },
  },
  extraReducers(builder) {
    builder.addCase(fetchVerktoyListeThunk.pending, (state, action) => {
      state.verktoyListeLoading = true;
      state.verktoyFilters = action.meta.arg.verktoyFilters ? action.meta.arg.verktoyFilters : initialState.verktoyFilters;
      state.isOppgaveEnrollmentSuccess = undefined;
    });
    builder.addCase(fetchVerktoyListeThunk.fulfilled, (state, action) => {
      state.verktoyListe = action.payload.Verktoyliste ? action.payload.Verktoyliste : [];
      state.verktoyListeLoading = false;
    });
    builder.addCase(fetchVerktoyListeThunk.rejected, (state, action) => {
      state.error = action.payload;
      state.verktoyListeLoading = false;
    });
    builder.addCase(fetchMineVerktoyV2Thunk.pending, state => {
      state.laeringsaktiviteterLoading = true;
      state.isOppgaveEnrollmentSuccess = undefined;
    });
    builder.addCase(fetchMineVerktoyV2Thunk.fulfilled, (state, action) => {
      state.laeringsaktiviteterLoading = false;
      state.mineVerktoyListe = action.payload.Verktoy;
    });
    builder.addCase(fetchMineVerktoyV2Thunk.rejected, (state, action) => {
      state.error = action.payload;
      state.laeringsaktiviteterLoading = false;
    });
    builder.addCase(fetchMittVerktoyByGuidThunk.pending, state => {
      state.mittVerktoyByGuidLoading = true;
    });
    builder.addCase(fetchMittVerktoyByGuidThunk.fulfilled, (state, action) => {
      state.mittVerktoyByGuidLoading = false;
      const payload = action.payload as unknown as {
        LaeringsaktivitetV2?: MittVerktoyItem;
        MittVerktoy?: MittVerktoyItem;
      };
      state.mittVerktoyByGuid = payload.LaeringsaktivitetV2 ?? payload.MittVerktoy;
    });
    builder.addCase(fetchMittVerktoyByGuidThunk.rejected, (state, action) => {
      state.error = action.payload;
      state.mittVerktoyByGuidLoading = false;
    });
    builder.addCase(fetchVerktoyThunk.pending, state => {
      state.verktoyLoading = true;
      if (state.isOppgaveEnrollmentSuccess !== true) {
        state.isOppgaveEnrollmentSuccess = undefined;
      }
    });
    builder.addCase(fetchVerktoyThunk.fulfilled, (state, action) => {
      const verktoy = action.payload.Verktoy ?? undefined;
      if (verktoy && action.meta.arg.oppgaveId) {
        verktoy.Laeringsaktivitet = undefined;
      }
      state.verktoy = verktoy;
      state.verktoyLoading = false;
    });
    builder.addCase(fetchVerktoyThunk.rejected, (state, action) => {
      state.error = action.payload;
      state.verktoyLoading = false;
    });
    builder.addCase(addLaeringsaktivitetThunk.pending, state => {
      state.isWaitingForAktivitetResponse = true;
      state.isOppgaveEnrollmentSuccess = undefined;
    });
    builder.addCase(addLaeringsaktivitetThunk.fulfilled, (state, action) => {
      if (action.meta.arg?.oppgaveId) {
        state.isOppgaveEnrollmentSuccess = true;
        if (state.verktoy && state.verktoy.VerktoyId === action.payload.VerktoyId) {
          state.verktoy = action.payload;
        }
      }
      state.isWaitingForAktivitetResponse = false;
      state.addedAktivitetName = action.meta.arg.verktoy ? action.meta.arg.verktoy.Navn : '';
      if (action.meta.arg.verktoy) {
        state.mineVerktoyListe.push(mapToMittVerktoyItem(action.payload));
      }
    });
    builder.addCase(addLaeringsaktivitetThunk.rejected, (state, action) => {
      if (!action.meta.arg?.oppgaveId) {
        state.isOppgaveEnrollmentSuccess = false;
      }
      state.error = action.payload;
      state.isWaitingForAktivitetResponse = false;
    });

    builder.addCase(avvisOppgaveLaeringsaktivitetThunk.pending, state => {
      state.isOppgaveAvvisSuccess = undefined;
      state.isOppgaveEnrollmentSuccess = undefined;
    });
    builder.addCase(avvisOppgaveLaeringsaktivitetThunk.fulfilled, state => {
      state.isOppgaveAvvisSuccess = true;
    });
    builder.addCase(avvisOppgaveLaeringsaktivitetThunk.rejected, (state, action) => {
      state.isOppgaveAvvisSuccess = false;
      state.error = action.payload;
    });
    builder.addCase(removeLaeringsaktivitetThunk.pending, state => {
      state.aktivitetToRemove = undefined;
      state.aktivitetToRemoveMineVerktoy = undefined;
      state.isWaitingForAktivitetResponse = true;
    });
    builder.addCase(removeLaeringsaktivitetThunk.fulfilled, (state, action) => {
      const nyLaeringsaktivitetListeMineVerktoy = state.mineVerktoyListe.filter(item => {
        return item.LaeringsaktivitetId && item.LaeringsaktivitetId !== action.payload.aktivitetsId;
      });
      state.isWaitingForAktivitetResponse = false;
      state.mineVerktoyListe = nyLaeringsaktivitetListeMineVerktoy;
      if (state.verktoy?.Laeringsaktivitet?.LaeringsaktivitetId === action.payload.aktivitetsId) {
        state.verktoy = { ...state.verktoy, Laeringsaktivitet: undefined };
      }
    });
    builder.addCase(removeLaeringsaktivitetThunk.rejected, (state, action) => {
      state.isWaitingForAktivitetResponse = false;
      state.error = action.payload;
    });
  },
});
export const verktoyReducer = reducer.reducer;
export const {
  clearError,
  setIsFilterOpen,
  closeLightboxes,
  setStartAktivitet,
  setVerktoyFilters,
  showRemoveAktivitetConfirmation,
  showRemoveAktivitetConfirmationMineVerktoy,
} = reducer.actions;
